import { Mutex } from "async-mutex";
import { BaseEventInput, BaseProjectionResult, EventProcessor } from "./EventProcessor";
import { EventProcessorRegistry } from "./EventProcessorRegistry";
import { BaseEntity, BaseEvent, BaseProjectMetadata } from "./structures";
import { UndoRedoStack } from "./UndoRedoStack";

export class BaseProjectWorld<
    TEventType extends number,
    TEventInputs extends BaseEventInput,
    TLocalEvent extends BaseEvent<TEventType>,
    TEntityTypeName extends string,
    TEntityType extends BaseEntity<TEventType>,
    TProjectionResult extends BaseProjectionResult,
    TServerEventType,
    TMetadata extends BaseProjectMetadata
> {
    projectMetadata: TMetadata;

    eventProcessors: EventProcessorRegistry<TEventType, TEventInputs, TLocalEvent, TProjectionResult, TServerEventType> =
        new EventProcessorRegistry<TEventType, TEventInputs, TLocalEvent, TProjectionResult, TServerEventType>();

    undoRedoStack: UndoRedoStack<TLocalEvent> = new UndoRedoStack<TLocalEvent>();

    dirtyEntities: Map<string, Set<TEntityType>> = new Map<string, Set<TEntityType>>();

    createLocalEvent = (eventProcessor: EventProcessor<
        TEventType,
        TEventInputs,
        TLocalEvent,
        TProjectionResult,
        TLocalEvent,
        TServerEventType
    >, utcCurrentTime: number, clientId: string, userId: string | undefined): TLocalEvent => {
        return {
            pi: this.projectMetadata.id,
            //Make sure to omit this so it can be auto generated: 
            // cx: undefined, //Will be auto-generated
            ex: 0, //Event index from server
            bx: 0, //Batch index from server
            tp: eventProcessor.eventType,
            tm: eventProcessor.eventTypeMinorVersion,
            et: utcCurrentTime,
            ci: clientId,
            ui: userId,
        } as TLocalEvent;
    }

    copyAndClearDirtyEntities(): Map<string, Set<TEntityType>> {
        const dirtyEntitiesCopy = new Map<string, Set<TEntityType>>();
        this.dirtyEntities.forEach((entitySet, key) => {
            dirtyEntitiesCopy.set(key, new Set(entitySet));
        });
        this.dirtyEntities.clear();
        return dirtyEntitiesCopy;
    }

    private hasAllAvailableProcessors = (events: TLocalEvent[]): boolean => {
        const uniqueTypes = new Set<TEventType>();

        for (const event of events) {
            if (uniqueTypes.has(event.tp)) continue;

            uniqueTypes.add(event.tp);
            const processor = this.eventProcessors.getProcessor(event.tp);
            if (processor === undefined) return false
        }

        return true;
    };

    get isExistOnServer() {
        return this.projectMetadata.serverEventsUpTo > 0;
    }

    private _writeMutex = new Mutex();
    private _readMutex = new Mutex();

    concurrentServerReading = async (readFromServer: (projectId: string) => Promise<TLocalEvent[]>): Promise<TLocalEvent[]> => {
        if (!this.projectMetadata.userOptInForServer) return [];

        let localEvents: TLocalEvent[] = [];
        await this._readMutex.runExclusive(async () => {
            localEvents = await readFromServer(this.projectMetadata.id);
        });
        return localEvents;
    }

    concurrentServerWriting = async (getLocalEventsNotOnServer: (projectId: string, li: number) => Promise<TLocalEvent[]>,
        writeToServer: (projectId: string, enforce_aggregate_concurrency: boolean, serverEvents: TServerEventType[]) => Promise<void>) => {
        if (!this.projectMetadata.userOptInForServer) return;

        await this._writeMutex.runExclusive(async () => {
            const eventsNotOnServer = await getLocalEventsNotOnServer(this.projectMetadata.id, this.projectMetadata.sentServerEventsUpTo);
            if (eventsNotOnServer.length === 0) return;

            const previousSentServerEventsUpTo = this.projectMetadata.sentServerEventsUpTo;
            this.projectMetadata.sentServerEventsUpTo = eventsNotOnServer[eventsNotOnServer.length - 1].cx!;

            const serverEventsPromises = eventsNotOnServer.map(event =>
                this.eventProcessors.getProcessor(event.tp)!.toServerEvent(event)
            );
            const serverEventResults = await Promise.all(serverEventsPromises);
            const serverEvents = serverEventResults.filter(x => x !== undefined) as TServerEventType[];

            if (serverEvents.length === 0) return;

            try {
                await writeToServer(this.projectMetadata.id, this.projectMetadata.enforceAggregateConcurrency, serverEvents);
            } catch (error: unknown) {
                this.projectMetadata.sentServerEventsUpTo = previousSentServerEventsUpTo;
                throw error;
            }
        });
    }

    reprojectAllEvents = (events: TLocalEvent[], newProjectionVersion: string) => {
        this.projectMetadata.projectionVersion = newProjectionVersion;
        this.projectMetadata.numberOfEvents = 0;
        this.projectMetadata.modifiedOn = 0;
        this.projectMetadata.modifiedByClientId = "";
        this.projectMetadata.modifiedByUserId = undefined;
        this.projectMetadata.createdOn = 0;
        this.projectMetadata.createdByClientId = "";
        this.projectMetadata.createdByUserId = undefined;

        for (const event of events) {
            const processor = this.eventProcessors.getProcessor(event.tp);
            if (processor === undefined) continue;

            processor.projectOntoWorld(event, false, false);

            this.newEventAdded(event);
        }
    }

    processEventsIntoWorldWithoutUndo = (events: TLocalEvent[]): TProjectionResult[] => {
        return this.processEventsIntoWorld(events, true, undefined, undefined, 0);
    }

    processEventsIntoWorld = (events: TLocalEvent[], isUndoAction: boolean, currentClientId: string | undefined, currentUserId: string | undefined, currentUtcLocalTime: number): TProjectionResult[] => {
        if (!events.length || !this.hasAllAvailableProcessors(events)) return [];

        const mutations: TProjectionResult[] = [];

        for (const event of events) {

            this.newEventAdded(event);

            const processor = this.eventProcessors.getProcessor(event.tp);

            const mutationResult = processor!.projectOntoWorld(event, true, true);
            if (mutationResult === undefined) continue;
            mutations.push(mutationResult);

            if (event.bx !== 0 || isUndoAction) continue;

            let undoEvent = {
                pi: this.projectMetadata.id,

                //Must leave out to auto generate
                //cx: undefined,
                ex: 0,
                bx: 0,
                tp: processor!.undoEventType,
                tm: processor!.undoEventTypeMinorVersion,
                et: currentUtcLocalTime,
                ci: currentClientId,
                ui: currentUserId,
            } as TLocalEvent;

            const undoEventSuccess = processor!.createUndoEvent(undoEvent, mutationResult);
            if (!undoEventSuccess) continue;

            this.undoRedoStack.addEventIntoUndoStack(event, undoEvent);
        }

        return mutations;
    };

    addEntityToSave = (entityType: TEntityTypeName, entity: TEntityType) => {
        if (this.dirtyEntities.has(entityType)) {
            this.dirtyEntities.get(entityType)?.add(entity);
        } else {
            this.dirtyEntities.set(entityType, new Set([entity]));
        }
    }

    constructor(initialMetadata: TMetadata) {
        this.projectMetadata = initialMetadata;
    }

    private newEventAdded(event: TLocalEvent) {

        this.projectMetadata.numberOfEvents++;

        if (event.et > this.projectMetadata.modifiedOn) {
            this.projectMetadata.modifiedByClientId = event.ci;
            this.projectMetadata.modifiedByUserId = event.ui;
            this.projectMetadata.modifiedOn = event.et;
        }

        if (this.projectMetadata.createdByClientId === undefined) {
            this.projectMetadata.createdByClientId = event.ci;
            this.projectMetadata.createdByUserId = event.ui;
            this.projectMetadata.createdOn = event.et;
        }

        if (!this.projectMetadata.userOptInForServer) {
            this.projectMetadata.userOptInForServer = event.bx > 0;
        }

        if (event.bx !== undefined && event.bx > this.projectMetadata.serverEventsUpTo) {
            this.projectMetadata.serverEventsUpTo = event.bx;
        }
    }

    // Method to register all event processors with proper typing
    registerProcessors(processors: Array<EventProcessor<
        TEventType,
        TEventInputs,
        TLocalEvent,
        TProjectionResult,
        TLocalEvent,
        TServerEventType
    >>) {
        processors.forEach(processor => {
            this.eventProcessors.registerProcessor(processor);
        });
    }
}